import React, { useState, useEffect, useRef } from "react";

import {
  Avatar,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import Typography from "@mui/joy/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "axios";
import dayjs from "dayjs";

import { IMaskInput } from "react-imask";
import Input from "@mui/material/Input";

import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useUtil } from "../../contexts/UtilContext";

import styles from "./UserProfile.module.css";
import { eighteenYearsAgo, isValidEmail } from "../../util/utilFunctions";

// --- remove top-level react-editor-js + tools imports ---
// import { createReactEditorJS } from "react-editor-js";
// import { EDITOR_JS_TOOLS } from "../Editor/tools";
import edjsHTML from "editorjs-html";
import DOMPurify from "dompurify";

const edjsParser = edjsHTML();

// ---------- DOMPurify: allow safe inline styles for colored/highlighted text ----------
const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "hr",
    "code",
    "span",
    "mark",
  ],
  ALLOWED_ATTR: ["href", "rel", "target", "style", "class"],
};

// avoid double-attaching hook on hot-reload
if (typeof window !== "undefined" && !window.__PURIFY_STYLE_HOOK_ATTACHED__) {
  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const allowed = [
        "color",
        "background-color",
        "text-decoration",
        "font-weight",
        "font-style",
      ];
      const safe = (data.attrValue || "")
        .split(";")
        .map((s) => s.trim())
        .filter((s) => {
          const [prop] = s.split(":").map((x) => x.trim().toLowerCase());
          return allowed.includes(prop);
        })
        .join("; ");
      if (safe) data.attrValue = safe;
      else data.keepAttr = false;
    }
  });
  window.__PURIFY_STYLE_HOOK_ATTACHED__ = true;
}

// Phone mask
const TextMaskCustom = React.forwardRef(function TextMaskCustom(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="(#00) 000-0000"
      definitions={{ "#": /[1-9]/ }}
      inputRef={ref}
      unmask={true}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

// EditorJS -> HTML
const edjsToHtml = (data) => {
  if (!data) return "";
  const input = data.blocks ? data : { blocks: data };
  try {
    const out = edjsParser.parse(input);
    if (Array.isArray(out)) return out.join("");
    if (typeof out === "string") return out;
    if (out && typeof out === "object" && typeof out.html === "string") {
      return out.html;
    }
    return "";
  } catch (e) {
    console.warn("EditorJS->HTML parse failed:", e);
    return "";
  }
};

// returns true if there are no meaningful blocks
const isEditorEmpty = (data) => {
  if (!data || !Array.isArray(data.blocks)) return true;
  if (data.blocks.length === 0) return true;
  const nonEmpty = data.blocks.some((b) => {
    if (!b) return false;
    if (b.type === "paragraph") {
      const t = (b.data?.text || "")
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
      return t.length > 0;
    }
    return true;
  });
  return !nonEmpty;
};

// HTML -> plain text (for `bio` fallback)
const htmlToPlainText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").trim();
};

// Text -> minimal HTML
const textToDisplayHtml = (text) => {
  if (!text) return "";
  const withLinks = String(text).replace(
    /(https?:\/\/[^\s)]+)(?![^<]*>)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  return withLinks.replace(/\n/g, "<br/>");
};

// Legacy plain text -> Editor.js blocks
const textToBlocks = (text) => ({
  time: Date.now(),
  blocks: (text ? String(text) : "").split(/\r?\n\s*\r?\n/).map((p) => ({
    type: "paragraph",
    data: { text: p.replace(/\r?\n/g, "<br>") },
  })),
  version: "2.28.0",
});

function UserProfile() {
  const { accessToken, logoutUser, updateProfilePic } = useAuth();
  const { showLoader, hideLoader } = useUtil();
  const navigate = useNavigate();

  const [ppUrl, setPPUrl] = useState();

  const [formState, setFormState] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    screenName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
    country: "",
    bio: "",
  });

  // --- EditorJS wrapper + tools (lazy, client-only)
  const [ReactEditorJS, setReactEditorJS] = useState(null);
  const [editorTools, setEditorTools] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    (async () => {
      // load wrapper
      const m = await import("react-editor-js");
      const Comp = m.createReactEditorJS();
      setReactEditorJS(() => Comp);

      // load tools (expects you to export getEditorTools from ../Editor/tools)
      const { getEditorTools } = await import("../Editor/tools");
      setEditorTools(await getEditorTools());
    })();
  }, []);

  // Editor.js state for bio
  const [bioBlocks, setBioBlocks] = useState(null);
  const [bioPreviewHtml, setBioPreviewHtml] = useState("");

  const handleDOBChange = (value) => {
    setFormState((prev) => ({ ...prev, dateOfBirth: value }));
  };

  const handleInputChange = (event) => {
    const { name, value, type, files } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const isValidPhone10or11 = (s = "") => {
    if (/^\d{10}$/.test(s)) return true;
    if (/^1\d{10}$/.test(s)) return true;
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.firstName)
      return toast.error("Please provide your First Name");
    if (!formState.lastName)
      return toast.error("Please provide your Last Name");
    if (!formState.email) return toast.error("Please provide your Email");
    if (!isValidEmail(formState.email))
      return toast.error("Please provide a valid Email format");
    if (formState.phoneNumber && !isValidPhone10or11(formState.phoneNumber))
      return toast.error("Please provide a valid Phone Number");
    if (formState.screenName && formState.screenName.length > 12)
      return toast.error("Screen Name should not exceed 12 characters");

    try {
      showLoader();

      const editorData = bioBlocks || {
        time: Date.now(),
        blocks: [],
        version: "2.28.0",
      };

      const empty = isEditorEmpty(editorData);

      let bio_html = null;
      let bio = "";
      let bio_json = null;

      if (!empty) {
        const rawHtml = edjsToHtml(editorData);
        const safeHtml = DOMPurify.sanitize(rawHtml, SANITIZE_OPTS);
        const plainText = htmlToPlainText(safeHtml);

        bio_html = safeHtml;
        bio = plainText;
        bio_json = JSON.stringify(editorData);
      } else {
        bio_html = null;
        bio = "";
        bio_json = null;
      }

      const userPayload = {
        firstName: formState.firstName,
        lastName: formState.lastName,
        middleName: formState.middleName,
        email: formState.email,
        phoneNumber: formState.phoneNumber,
        screenName: formState.screenName,
        dateOfBirth: formState.dateOfBirth
          ? formState.dateOfBirth.format("YYYY-MM-DD")
          : null,
        gender: formState.gender,
        bio,
        bio_html,
        bio_json,
      };

      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/user/details/save`,
        userPayload,
        { headers: { Authorization: accessToken } }
      );

      if (res.status === 200 && res.data?.success) {
        toast.success(
          empty ? "Bio cleared." : "User details updated successfully!"
        );
        if (empty) {
          setBioPreviewHtml("");
          setBioBlocks({ time: Date.now(), blocks: [], version: "2.28.0" });
          setFormState((p) => ({ ...p, bio: "" }));
        }
      } else {
        toast.error(
          res.data?.message ||
            "Some error occurred while saving your details. Please try again!"
        );
      }
    } catch (error) {
      toast.error("Error saving details: " + (error?.message || String(error)));
    } finally {
      hideLoader();
    }
  };

  const handleLogout = async (event) => {
    event.preventDefault();
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/user/logout`,
        {},
        { headers: { Authorization: accessToken } }
      );

      if (response.status === 200 && response.data?.success) {
        toast.success("User successfully logged out!");
        logoutUser();
        navigate(`/`);
      } else {
        const errorMsg =
          response.data?.message ||
          "Some error occurred while saving your details. Please try again!";
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Error saving the user details:", error);
    }
  };

  // Profile image
  const hiddenFileInput = useRef(null);
  const editProfilePic = () => hiddenFileInput.current?.click();

  const handleImageChange = async (event) => {
    try {
      showLoader();
      const fileUploaded = event.target.files[0];
      const formData = new FormData();
      formData.append("image", fileUploaded);

      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/user/upload-pp`,
        formData,
        { headers: { Authorization: accessToken } }
      );

      if (response.status === 200 && response.data?.success) {
        toast.success("Image uploaded successfully!");
        if (response.data.photoUrl) {
          updateProfilePic(response.data.photoUrl);
          setPPUrl(response.data.photoUrl);
        }
      } else {
        const msg =
          response.data?.message ||
          "Some error occurred while saving your image. Please try again!";
        toast.error(msg);
      }
    } catch (error) {
      toast.error("Error saving the user image:", error);
    } finally {
      hideLoader();
    }
  };

  // Fetch user
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        showLoader();

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/user/details`,
          {},
          { headers: { Authorization: accessToken } }
        );

        if (response.status === 200 && response.data?.success) {
          const userLiveData = response.data.user;

          setFormState((prev) => ({
            ...prev,
            firstName: userLiveData.first_name || "",
            middleName: userLiveData.middle_name || "",
            lastName: userLiveData.last_name || "",
            screenName: userLiveData.screen_name || "",
            email: userLiveData.email || "",
            phoneNumber:
              typeof userLiveData.phone_number === "string"
                ? userLiveData.phone_number.replace(/\D/g, "")
                : userLiveData.phone_number || "",
            dateOfBirth: userLiveData.date_of_birth
              ? dayjs(String(userLiveData.date_of_birth).slice(0, 10))
              : "",
            gender: userLiveData.gender || "",
            bio: userLiveData.bio || "",
          }));

          setPPUrl(userLiveData.photo_url || null);

          let blocks;
          if (userLiveData.bio_json) {
            try {
              blocks =
                typeof userLiveData.bio_json === "string"
                  ? JSON.parse(userLiveData.bio_json)
                  : userLiveData.bio_json;
            } catch (e) {
              console.warn("bio_json parse failed; falling back to TEXT", e);
              blocks = textToBlocks(userLiveData.bio || "");
            }
          } else {
            blocks = textToBlocks(userLiveData.bio || "");
          }
          setBioBlocks(blocks);

          const previewHtml = userLiveData.bio_html
            ? DOMPurify.sanitize(userLiveData.bio_html, SANITIZE_OPTS)
            : textToDisplayHtml(userLiveData.bio || "");
          setBioPreviewHtml(previewHtml);
        } else {
          const errorMsg =
            response.data?.message ||
            "Some error occurred while fetching your details. Please try again!";
          toast.error(errorMsg);
        }
      } catch (error) {
        toast.error(
          "Error during validation: " + (error?.message || String(error))
        );
      } finally {
        hideLoader();
      }
    };

    if (!accessToken) return;
    fetchUserData();
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Specialization dialog (unchanged demo)
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <div className={styles.container}>
      <div className={styles.containerTitle}>
        <h1>User Profile</h1>
      </div>

      {/* Hidden file input for profile photo */}
      <input
        type="file"
        onChange={handleImageChange}
        ref={hiddenFileInput}
        style={{ display: "none" }}
      />

      {/* Profile photo */}
      <div className={styles.ppSection}>
        <div className={styles.ppExisting}>
          {ppUrl ? (
            <img src={ppUrl} alt="Profile Pic" className={styles.ppImage} />
          ) : (
            <Avatar className={styles.ppAvatar} alt="Profile Pic" src="" />
          )}
          <div className={styles.ppEdit} onClick={editProfilePic}>
            <EditIcon className={styles.ppEditIcon} />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className={styles.formSection}>
        <div className={styles.formComponent}>
          <TextField
            id="firstName"
            name="firstName"
            label="First Name"
            variant="outlined"
            required
            type="text"
            fullWidth
            value={formState.firstName}
            onChange={handleInputChange}
          />
        </div>

        <div className={`${styles.formComponent} ${styles.componentType1}`}>
          <TextField
            id="middleName"
            name="middleName"
            label="Middle Name"
            variant="outlined"
            type="text"
            fullWidth
            value={formState.middleName}
            onChange={handleInputChange}
          />
          <TextField
            id="lastName"
            name="lastName"
            label="Last Name"
            variant="outlined"
            required
            type="text"
            fullWidth
            value={formState.lastName}
            onChange={handleInputChange}
          />
        </div>

        <div className={`${styles.formComponent} ${styles.componentType2}`}>
          <TextField
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            required
            type="email"
            fullWidth
            value={formState.email}
            onChange={handleInputChange}
          />
          <TextField
            id="phoneNumber"
            name="phoneNumber"
            label="Phone Number"
            variant="outlined"
            type="text"
            fullWidth
            value={formState.phoneNumber}
            onChange={handleInputChange}
            InputProps={{ inputComponent: TextMaskCustom }}
          />
        </div>

        <div className={`${styles.formComponent} ${styles.componentType3}`}>
          <TextField
            id="screenName"
            name="screenName"
            label="Screen Name"
            variant="outlined"
            type="text"
            fullWidth
            value={formState.screenName}
            onChange={handleInputChange}
          />
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                id="dateOfBirth"
                name="dateOfBirth"
                label="DOB"
                maxDate={eighteenYearsAgo}
                value={formState.dateOfBirth}
                onChange={handleDOBChange}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Gender</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="gender"
              name="gender"
              value={formState.gender}
              label="Gender"
              onChange={handleInputChange}
            >
              <MenuItem value={"Male"}>Male</MenuItem>
              <MenuItem value={"Female"}>Female</MenuItem>
              <MenuItem value={"Other"}>Other</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* --- Bio Editor.js --- */}
        <div className={styles.formComponent}>
          <div className={styles.bioEditorLabel}>Bio</div>

          {ReactEditorJS && editorTools && bioBlocks && (
            <ReactEditorJS
              tools={editorTools}
              defaultValue={bioBlocks}
              placeholder="Write your bio… Use bullets, numbers, headings, links, etc."
              onChange={async (api) => {
                const data = await api.saver.save();
                setBioBlocks(data);
                const html = edjsToHtml(data);
                const safe = DOMPurify.sanitize(html, SANITIZE_OPTS);
                setBioPreviewHtml(safe);
              }}
            />
          )}

          {/* Read-only preview (display) */}
          {bioPreviewHtml && (
            <div
              className={styles.bioDisplay}
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: "1px solid #eee",
              }}
              dangerouslySetInnerHTML={{ __html: bioPreviewHtml }}
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className={styles.formComponent}>
        <div className={styles.formAction}>
          <Button variant="contained" size="large" onClick={handleSubmit}>
            Save Details
          </Button>
        </div>
      </div>
      <div className={styles.formComponent}>
        <div className={styles.formAction}>
          <Button
            variant="contained"
            size="large"
            color="error"
            onClick={handleLogout}
          >
            Logout
          </Button>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{"Detailed specialization information"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <ul>
              <li>
                <strong>Residential Real Estate</strong>
                <ul>
                  <li>Single-Family Homes</li>
                  <li>Luxury Properties</li>
                  <li>Condos & Townhouses</li>
                  <li>First-Time Homebuyers</li>
                </ul>
              </li>
              <li>
                <strong>Commercial Real Estate</strong>
                <ul>
                  <li>Office Buildings</li>
                  <li>Retail</li>
                  <li>Industrial</li>
                  <li>Multi-Family</li>
                </ul>
              </li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default UserProfile;
